import React, { useState } from "react";
import { Box, Button, TextField, Typography, Autocomplete, TextareaAutosize, Divider } from "@mui/material";
import { FaCheck, FaTimes } from "react-icons/fa";
import countries from "../../utils/countries";
import styled from "styled-components";
import BASE_API_URL from '../../apis/index';
import axios from "axios";

const CompanyInfoSection = ({ formData, setFormData, handleChange, fieldValidation, setFieldValidation }) => {
    const [mobileError, setMobileError] = useState('');
    const [emailError, setEmailError] = useState('');

    const handleMobileNumAdding = () => {
        const mobile = formData.mobileInput.trim();
        const countryCode = formData.countryCode || "+91"; // default code

        const mobileRegex = /^(?:[6-9]\d{9}|\d{8}|\d{11})$/;

        if (!mobileRegex.test(mobile)) {
            setMobileError("Please enter a valid mobile (10-digit) or landline (8 or 11-digit) number");
            return;
        }

        const fullNumber = `${countryCode} ${mobile}`;

        if (formData.mobileNumbers.includes(fullNumber)) {
            setMobileError("Number already added");
            return;
        }

        setFormData({
            ...formData,
            mobileNumbers: [...formData.mobileNumbers, fullNumber],
            mobileInput: "",
        });
        setMobileError("");
    };

    const handleEmailAdding = () => {
        const email = formData.emailInput.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError('Please enter a valid email address');
            return;
        }
        if (formData.emails.includes(email)) {
            setEmailError('Email already added');
            return;
        }
        setFormData({
            ...formData,
            emails: [...formData.emails, email],
            emailInput: ''
        });
        setEmailError('');
    };

    const handleCompanyTypeToggle = (type) => {
        const updated = formData.companyTypes.includes(type)
            ? formData.companyTypes.filter((t) => t !== type)
            : [...formData.companyTypes, type];
        setFormData({ ...formData, companyTypes: updated });
    };

    const handleCompanyNameBlur = async () => {
        if (!formData.companyName.trim()) return;

        try {
            const authToken = localStorage.getItem("authToken");

            const res = await axios.post(
                `${BASE_API_URL}/api/seller/company/check-name-exist`,
                { companyName: formData.companyName },
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            if (res.data.exists) {
                setFieldValidation(prev => ({
                    ...prev,
                    companyName: {
                        status: 'error',
                        message: res.data.message
                    }
                }));
            } else {
                setFieldValidation(prev => ({
                    ...prev,
                    companyName: {
                        status: 'success',
                        message: ''
                    }
                }));
            }

        } catch (err) {
            console.error(err);
        }
    };

    const removeFieldItem = (type, item) => { setFormData({ ...formData, [type]: formData[type].filter((i) => i !== item) }); };

    return (
        <Wrapper>
            <Typography variant="h6" className="mb-3">Company Info</Typography>

            <ImagesUploader formData={formData} setFormData={setFormData} />

            <div className="row gx-2 mt-3">
                <div className="col-md-4">
                    <TextField
                        className="w-100"
                        // required
                        label={<> Company Name <span style={{ color: "red" }}>*</span> </>}
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        onBlur={handleCompanyNameBlur}
                        error={fieldValidation.companyName.status === 'error'}
                        helperText={fieldValidation.companyName.message}
                        color={fieldValidation.companyName.status === 'success' ? 'success' : 'primary'}
                        margin="none"
                    />
                </div>

                <div className="col-md-4">
                    <Autocomplete
                        className="w-100"
                        options={countries}
                        getOptionLabel={(option) => option?.label || ""}
                        renderInput={(params) => (
                            <TextField {...params} label={<> Country of Origin <span style={{ color: "red" }}>*</span> </>} />
                        )}
                        value={formData.countryOfOrigin || null}
                        onChange={(event, newValue) =>
                            setFormData({ ...formData, countryOfOrigin: newValue })
                        }
                    />
                </div>

                <div className="col-md-4">
                    <TextField
                        className="w-100"
                        label={<> Address <span style={{ color: "red" }}>*</span> </>}
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <div className="row gx-2 mt-3">
                {/* Mobile Number */}
                <div className="col-md-4">
                    <div className="position-relative d-flex align-items-start gap-2">
                        {/* Country Code Select */}
                        <TextField
                            select
                            label="Code"
                            value={formData.countryCode || "+91"}
                            onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                            SelectProps={{ native: true }}
                            sx={{ width: "45%" }}
                        >
                            {[
                                { code: "+1", label: "US (+1)" },
                                { code: "+44", label: "UK (+44)" },
                                { code: "+91", label: "IN (+91)" },
                                { code: "+971", label: "UAE (+971)" },
                            ].map((opt) => (
                                <option key={opt.code} value={opt.code}> {opt.label} </option>
                            ))}
                        </TextField>

                        {/* Mobile Input */}
                        <TextField
                            className="w-100"
                            label={<>Mobile Number <span style={{ color: "red" }}>*</span></>}
                            name="mobileInput"
                            value={formData.mobileInput}
                            onChange={handleChange}
                            onBlur={() => {
                                if (formData.mobileInput?.trim()) {
                                    handleMobileNumAdding(formData.countryCode);
                                }
                            }}
                        />
                        <Button className="floatingAddButton" onClick={() => handleMobileNumAdding(formData.countryCode)}> Add </Button>
                    </div>

                    {mobileError && formData.mobileInput && (<span className="small text-danger"> {mobileError} </span>)}

                    <DynamicList>
                        {formData.mobileNumbers.map((num, idx) => (
                            <span key={idx} className="pill">
                                {num} <FaTimes onClick={() => removeFieldItem("mobileNumbers", num)} />
                            </span>
                        ))}
                    </DynamicList>
                </div>

                {/* Email */}
                <div className="col-md-4">
                    <div className="position-relative">
                        <TextField
                            className="w-100"
                            label={<> Add Email <span style={{ color: "red" }}>*</span> </>}
                            name="emailInput"
                            value={formData.emailInput}
                            onChange={handleChange}
                            onBlur={() => {
                                if (formData.emailInput?.trim()) {
                                    handleEmailAdding();
                                }
                            }}
                        />
                        <Button className="floatingAddButton" onClick={handleEmailAdding}>Add</Button>
                    </div>
                    {emailError && formData.emailInput && (<span className="small text-danger"> {emailError} </span>)}
                    <DynamicList>
                        {formData.emails.map((email, idx) => (
                            <span key={idx} className="pill">{email}<FaTimes onClick={() => removeFieldItem("emails", email)} /></span>
                        ))}
                    </DynamicList>
                </div>

                {/* Company Logo Upload */}
                <div className="col-md-4">
                    <Box sx={{ p: 1 }}>
                        <div className="d-flex">
                            <Typography variant="subtitle1" className="me-2 mb-0" gutterBottom>Company Logo <span style={{ color: "red" }}>*</span></Typography>
                            {formData.existingCompanyLogo && typeof formData.existingCompanyLogo === 'string' ? (
                                <div className="filePreview">
                                    <img src={`${BASE_API_URL}/uploads/user_comp_docs/${formData.existingCompanyLogo}`} alt="Logo" width="100" />
                                    <Button size="small" color="error" onClick={() => setFormData({ ...formData, existingCompanyLogo: null })}>
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <Button variant="outlined" component="label">
                                    Upload Logo
                                    <input
                                        type="file"
                                        name="companyLogo"
                                        accept="image/*"
                                        hidden
                                        required
                                        onChange={(e) =>
                                            setFormData({ ...formData, companyLogo: e.target.files[0] })
                                        }
                                    />
                                </Button>
                            )}

                        </div>
                        {formData.companyLogo && (
                            <Typography variant="body2" sx={{ mt: 1 }}>{formData.companyLogo.name}</Typography>
                        )}
                    </Box>
                </div>
            </div>

            {/* Company Type Pills */}
            <div className="d-flex align-items-center my-3">
                <Typography variant="subtitle1" className="me-3">Company Type<span style={{ color: "red" }}>*</span> : </Typography>
                <Pills>
                    {["Contract Manufacturer", "Ingredient Manufacturer", "Ingredient Distributor", "CRO"].map((type) => (
                        <Pill
                            key={type}
                            className={formData.companyTypes.includes(type) ? "selected" : ""}
                            onClick={() => handleCompanyTypeToggle(type)}
                        >
                            {formData.companyTypes.includes(type) && <FaCheck />} {type}
                        </Pill>
                    ))}
                </Pills>
            </div>

            <Typography variant="subtitle1">Company Brief<span style={{ color: "red" }}>*</span> : </Typography>
            <TextareaAutosize
                required
                minRows={2}
                placeholder="Company Profile / Brief Description"
                name="brief"
                value={formData.brief}
                onChange={handleChange}
                className="CompBriefField p-3 mt-2 rounded w-100"
            />
        </Wrapper>
    );
};

export default CompanyInfoSection;

const ImagesUploader = ({ formData, setFormData }) => {
    return (
        <Box
            sx={{
                mt: 4,
                p: 2,
                border: "2px dashed #ccc",
                borderRadius: "10px",
                textAlign: "center",
                bgcolor: "#fafafa",
                cursor: "pointer"
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
                e.preventDefault();
                const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith("image/"));

                const validFiles = files.filter(file => file.size <= 5 * 1024 * 1024); // max 5MB
                const invalidFiles = files.filter(file => file.size > 5 * 1024 * 1024);

                if (invalidFiles.length > 0) {
                    alert("Some files were not added because they exceed the 5MB limit.");
                }

                setFormData(prev => ({
                    ...prev,
                    companyImages: [...prev.companyImages, ...validFiles]
                }));
            }}
        >
            <Typography variant="subtitle1" gutterBottom>
                Company Image <span className="text-danger">*</span>
            </Typography>

            <Button variant="outlined" component="label">
                Select Images
                <input
                    type="file"
                    name="companyImages"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => {
                        const files = Array.from(e.target.files);

                        const validFiles = files.filter(file => file.size <= 5 * 1024 * 1024);
                        const invalidFiles = files.filter(file => file.size > 5 * 1024 * 1024);

                        if (invalidFiles.length > 0) {
                            alert("Some files were not added because they exceed the 5MB limit.");
                        }

                        setFormData(prev => ({
                            ...prev,
                            companyImages: [...prev.companyImages, ...validFiles]
                        }));
                    }}
                />
            </Button>

            {/* Indication message */}
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                Maximum image size allowed: 5MB
            </Typography>

            <div className="d-flex flex-wrap align-items-center gap-2">
                {/* Already uploaded images from server */}
                {formData.existingCompanyImages && formData.existingCompanyImages.length > 0 && (
                    <>
                        <DynamicList sx={{ mt: 2 }}>
                            {formData.existingCompanyImages.map((img, idx) => (
                                <span key={idx} className="compImagesPreview">
                                    <img
                                        src={`${BASE_API_URL}/uploads/user_comp_docs/${img}`}
                                        alt="Company"
                                    />
                                    <FaTimes
                                        onClick={() =>
                                            setFormData(prev => ({
                                                ...prev,
                                                existingCompanyImages: prev.existingCompanyImages.filter((_, i) => i !== idx)
                                            }))
                                        }
                                    />
                                </span>
                            ))}
                        </DynamicList>
                        <Divider orientation="vertical" variant="middle" flexItem className="mx-3" />
                    </>
                )}

                {/* Newly added images Preview */}
                {formData.companyImages.length > 0 && (
                    <DynamicList sx={{ mt: 2 }}>
                        {formData.companyImages.map((file, idx) => (
                            <span key={idx} className="compImagesPreview">
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    onLoad={() => URL.revokeObjectURL(file)} // cleanup memory
                                />
                                <FaTimes
                                    onClick={() =>
                                        setFormData(prev => ({
                                            ...prev,
                                            companyImages: prev.companyImages.filter((_, i) => i !== idx)
                                        }))
                                    }
                                />
                            </span>
                        ))}
                    </DynamicList>
                )}
            </div>
        </Box>
    )
}

const Wrapper = styled.div`
.floatingAddButton{
    border: 1px solid #b2b2b2ff;
    position: absolute;
    top: 50%;
    left: 90%;
    transform: translate(-50%, -50%);
}

.compImagesPreview{
    position: relative;
    img{
        width: 100px;
        height: 70px;
        object-fit: cover;
        border-radius: 5px;
    }
    svg{
        position: absolute;
        top: -10px;
        right: -10px;
        background: red;
        fill: white;
        padding: 3px;
        border-radius: 10px;
        width: 20px;
        height: 20px;
    }
}
`;

const Pills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    ${'' /* margin: 1rem 0; */}

    .selected {
        background: #e0ecff;
        border: 1px solid #4285f4;
        color: #1a73e8;
    }
`;

const Pill = styled.span`
    border: 1px solid #ccc;
    padding: 8px 12px;
    border-radius: 5px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
`;

const DynamicList = styled(Box)`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;

    .pill {
        display: flex;
        align-items: center;
        background: #f0f0f0;
        padding: 6px 10px;
        border-radius: 16px;
        gap: 6px;
        font-size: 0.9rem;
        border-radius: 5px;
    }

    svg {
        cursor: pointer;
        color: #888;
    }
`;
